import { useEffect, useState } from "react";
import axios from "axios";
import { Clock, MapPin, RotateCw, ArrowUpRight, Sparkles, Ticket, Wallet } from "lucide-react";
import { API, CAT_LABEL, placeImg } from "../utils/constants";
import { formatDuration } from "../utils/smartDuration";
import "./PlaceCard.css";
import { PhotoCredit } from "./PhotoCredit";

const CATEGORIES = ["culture", "food", "nightlife", "special", "popular", "hidden_gem"];

// The card the traveller reads comes from the server, one place at a time.
// Until it arrives (or when we hold no card for a place they wrote themselves)
// the front is drawn from what the list already knows.
// A card read once is kept for the visit: opening it again shows it at once.
const CARDS = new Map();
const cardKey = (place) => place.place_id || `${place.name}|${place.city || ""}`;

const useCard = (place) => {
  const [card, setCard] = useState(place?.card || (place && CARDS.get(cardKey(place))) || null);
  useEffect(() => {
    if (!place) return undefined;
    if (place.card) { setCard(place.card); return undefined; }
    const key = cardKey(place);
    if (CARDS.has(key)) { setCard(CARDS.get(key)); return undefined; }
    let alive = true;
    setCard(null);
    const params = place.place_id ? { place_id: place.place_id } : { name: place.name, city: place.city || "" };
    axios.get(`${API}/catalog/card`, { params })
      .then((res) => { const got = res.data.card || null; if (got) CARDS.set(key, got); if (alive) setCard(got); })
      .catch(() => { if (alive) setCard(null); });
    return () => { alive = false; };
  }, [place]);
  return card;
};

const Links = ({ links }) => (links?.length ? (
  <div className="pc-links">
    {links.map((l) => <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer">{l.label} ↗</a>)}
  </div>
) : null);

const Chips = ({ items }) => {
  const shown = (items || []).filter(Boolean);
  return shown.length ? <div className="pc-facts">{shown.map((t) => <span key={t} className="pc-chip">{t}</span>)}</div> : null;
};

const Tips = ({ tips }) => {
  const [all, setAll] = useState(false);
  const shown = all ? tips : tips.slice(0, 3);
  return (
    <>
      <ul className="pc-tips">
        {shown.map((t) => <li key={t.text}>{t.title && <b>{t.title}</b>}{t.text}</li>)}
      </ul>
      {tips.length > 3 && !all && <button className="pc-more" onClick={() => setAll(true)}>A few more tips</button>}
    </>
  );
};

// The tips by the moment they are for (before, on the way, at the door, inside, on the way out); the local's
// tip and what travellers learned stay beside them, each said once.
const TipsByMoment = ({ s }) => {
  const grouped = new Set((s.groups || []).flatMap((g) => g.tips));
  const others = (s.tips || []).filter((t) => !grouped.has(t.text) && (t.title === "From a local" || t.title === "What travellers learned"));
  return (
    <>
      {others.filter((t) => t.title === "From a local").map((t) => <p key={t.text} className="lead">{t.text}</p>)}
      {(s.groups || []).map((g) => (
        <div key={g.moment} className="pc-moment">
          <span className="pc-eyebrow">{g.label}</span>
          <ul className="pc-tips">{g.tips.map((t) => <li key={t}>{t}</li>)}</ul>
        </div>
      ))}
      {others.filter((t) => t.title !== "From a local").length > 0 && (
        <div className="pc-moment">
          <span className="pc-eyebrow">What travellers learned</span>
          <ul className="pc-tips">{others.filter((t) => t.title !== "From a local").map((t) => <li key={t.text}>{t.text}</li>)}</ul>
        </div>
      )}
    </>
  );
};

const ANSWERS = {
  time: (s) => (<>{s.text && <p>{s.text}</p>}{s.note && <p className="quiet">{s.note}</p>}</>),
  order: (s) => (<>{s.text && <p>{s.text}</p>}<Chips items={s.diet} /></>),
  tips: (s) => ((s.groups || []).length ? <TipsByMoment s={s} /> : <Tips tips={s.tips || []} />),
  best: (s) => (
    <>
      {s.arrive && <p className="lead">Be there between {s.arrive.replace("-", " and ")}</p>}
      {(s.advice || []).map((a) => <p key={a.why}>{a.when && <strong style={{ color: "var(--zen-text)" }}>{a.when}. </strong>}{a.why}</p>)}
      {!s.arrive && !(s.advice || []).length && s.hours && <p>A visit makes sense between {s.hours.replace("-", " and ")}.</p>}
      {s.season && <p className="quiet">{s.season}</p>}
      {(s.moments || []).map((m) => (
        <p key={m.when}><strong style={{ color: "var(--zen-text)" }}>{m.when}, {m.window.replace("-", "–")}. </strong>
          {m.why}{m.scope ? <span className="quiet"> ({m.scope})</span> : null}</p>
      ))}
      {(s.crowds || []).length > 0 && (
        <dl className="pc-week">
          {s.crowds.map((c) => [<dt key={`${c.when}-w`}>{c.when}</dt>, <dd key={`${c.when}-t`}>{c.text}</dd>])}
        </dl>
      )}
      {(s.seasons || []).map((n) => <p key={n.note} className="quiet">{n.months && <strong style={{ color: "var(--zen-sub)" }}>{n.months}. </strong>}{n.note}</p>)}
    </>
  ),
  open: (s) => (
    <>
      {typeof s.week === "string" && <p className="lead">{s.week}</p>}
      {Array.isArray(s.week) && (
        <dl className="pc-week">
          {s.week.map((d) => [<dt key={`${d.day}-d`}>{d.day}</dt>, <dd key={`${d.day}-h`}>{d.hours === "closed" ? "Closed" : d.hours}</dd>])}
        </dl>
      )}
      {s.last_entry && <p>Last entry {s.last_entry}</p>}
      {s.last_order && <p>Last order {s.last_order}</p>}
      {s.note && <p className={s.week ? "quiet" : undefined}>{s.note}</p>}
    </>
  ),
  cost: (s) => (
    <>
      {s.booking?.label && <p className="lead">{s.booking.label}{s.booking.ahead ? `, ${s.booking.ahead}` : ""}</p>}
      {s.booking?.how && <p>{s.booking.how}</p>}
      {(s.cost?.price || s.cost?.band) && <p className="lead">{[s.cost.price, s.cost.band].filter(Boolean).join(" · ")}</p>}
      {s.cost?.note && <p>{s.cost.note}</p>}
      <Chips items={[s.cash_only && "Cash only", ...(s.needs || [])]} />
    </>
  ),
  getting: (s) => (
    <>
      {s.arrival && <p>{s.arrival}</p>}
      {s.address && <p className="quiet">{s.address}</p>}
      <Chips items={[...(s.access?.lines || []), s.access?.children, s.access?.rain]} />
      {s.access?.note && <p className="quiet" style={{ marginTop: 10 }}>{s.access.note}</p>}
    </>
  ),
  mistakes: (s) => (
    <ul className="pc-tips">
      {(s.mistakes || []).map((m) => <li key={m}>{m}</li>)}
    </ul>
  ),
  nearby: (s, openPlace) => (
    <>
      {(s.ours || []).map((n) => (
        <button key={n.slug} className="pc-near" onClick={() => openPlace && openPlace({ name: n.name, place_id: n.place_id })}>
          <b>{n.name}</b>{n.walk_min ? <span className="quiet"> · {n.walk_min} min walk</span> : null}
          {n.why && <span className="pc-near-why">{n.why}</span>}
        </button>
      ))}
      {(s.tables || []).map((t) => (
        <div className="pc-table" key={t.name}>
          <b>{t.name}</b>{t.what ? ` · ${t.what}` : ""}
          {t.about && <p>{t.about}</p>}
          <p className="quiet">{[t.walk, t.hours].filter(Boolean).join(" · ")}</p>
          {t.map_url && <div className="pc-links" style={{ marginTop: 4 }}><a href={t.map_url} target="_blank" rel="noopener noreferrer">Map ↗</a></div>}
        </div>
      ))}
      {s.food && <p><strong style={{ color: "var(--zen-text)" }}>To eat. </strong>{s.food}</p>}
      {s.pairs_with && <p><strong style={{ color: "var(--zen-text)" }}>Goes well with. </strong>{s.pairs_with}</p>}
    </>
  ),
};

const Note = ({ label, text, quiet }) => (text ? (
  <div className={`pc-note${quiet ? " quiet" : ""}`}>
    <span className="pc-eyebrow" style={quiet ? { color: "var(--zen-dim)" } : undefined}>{label}</span>
    <p>{text}</p>
  </div>
) : null);

const PlaceCard = ({ place, footer }) => {
  const card = useCard(place);
  const [turned, setTurned] = useState(false);
  const [open, setOpen] = useState(null);
  const [nextDoor, setNextDoor] = useState(null);     // a place "near here" opened from this card
  useEffect(() => { setTurned(false); setOpen(null); setNextDoor(null); }, [place]);
  if (!place) return null;
  if (nextDoor) {
    return (
      <PlaceCard place={nextDoor} footer={
        <button className="pc-back-to" onClick={() => setNextDoor(null)}>← Back to {place.name}</button>
      } />
    );
  }

  const front = card?.front || {};
  const back = card?.back || {};
  const cat = CATEGORIES.includes(place.category) ? place.category : "popular";
  const opening = front.opening || place.description_short || place.description || place.punchline;
  const duration = front.duration || (place.duration ? `About ${formatDuration(place.duration)}` : "");
  const area = [front.area || place.neighborhood, front.city || place.city].filter(Boolean).join(", ");
  const mapUrl = card?.map_url
    || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${place.name}, ${place.city || "Japan"}`)}`;
  const hasBack = !!(back.story || back.known_for || back.why_chosen || back.locals_move || back.worth_knowing || back.consider);

  return (
    <div data-testid="place-card">
      <div className="pc-stage">
        <div className={`pc-rotator${turned ? " turned" : ""}`}>
          <div className="pc-face" aria-hidden={turned}>
            <div className="pc-media">
              {place.image && <PhotoCredit source={place.image_source} credit={place.image_credit} />}
              {place.image ? <img src={placeImg(place.image)} alt={place.name} /> : (
                <div className="w-full h-full flex flex-col items-center justify-center px-6 text-center"
                  style={{ background: "linear-gradient(135deg, var(--zen-s2) 0%, var(--zen-accent) 120%)" }}>
                  <Sparkles className="w-6 h-6 mb-2" style={{ color: "rgba(255,255,255,0.7)" }} />
                  <span className="text-sm font-medium leading-tight" style={{ color: "rgba(255,255,255,0.9)" }}>{place.name}</span>
                  {!card && <span className="text-[9px] mt-1.5 uppercase tracking-wider font-semibold" style={{ color: "rgba(255,255,255,0.5)" }}>from your wishlist</span>}
                </div>
              )}
              <span className={`zen-badge ${cat}`} style={{ fontSize: "8px", padding: "3px 8px", top: 12, left: 12 }}>
                {CAT_LABEL[cat] || "POPULAR"}
              </span>
            </div>
            <div className="pc-pad">
              <h3 className="font-heading pc-name" data-testid="modal-place-name">{place.name}</h3>
              {(front.name_ja || place.name_ja) && <p className="pc-name-ja">{front.name_ja || place.name_ja}</p>}
              {(front.title || place.title) && <p className="pc-title">{front.title || place.title}</p>}
              {opening && <p className="pc-copy" data-testid="modal-description">{opening}</p>}
              <div className="pc-meta">
                {duration && <span className="pc-chip"><Clock className="w-3 h-3" />{duration}</span>}
                {area && <span className="pc-chip"><MapPin className="w-3 h-3" />{area}</span>}
                {front.price && <span className="pc-chip"><Wallet className="w-3 h-3" />{front.price}</span>}
                {front.booking && <span className="pc-chip warm"><Ticket className="w-3 h-3" />{front.booking}</span>}
              </div>
              <Chips items={front.flags} />
              {front.good_for && <Note label="Good for" text={front.good_for} quiet />}
            </div>
          </div>

          <div className="pc-face pc-back" aria-hidden={!turned}>
            <div className="pc-pad">
              <span className="pc-eyebrow">{place.name}</span>
              <h3 className="font-heading pc-name" style={{ fontSize: 23 }}>{back.heading || "A little closer"}</h3>
              {back.known_for && <p className="pc-copy"><strong>{back.known_for}</strong></p>}
              <Note label="Why we chose it" text={back.why_chosen} />
              {back.story && <p className="pc-copy">{back.story}</p>}
              <Note label="What a local does" text={back.locals_move} />
              <Note label="Worth knowing" text={back.worth_knowing} />
              <Note label="Keep in mind" text={back.consider} quiet />
              {(back.not_to_confuse || []).length > 0 && <Note label="Not to confuse with" text={back.not_to_confuse.join(" · ")} quiet />}
            </div>
          </div>
        </div>
      </div>

      <div className="pc-footer">
        {hasBack ? (
          <button className="pc-turn" onClick={() => setTurned((t) => !t)} data-testid="turn-card-btn"
            aria-label={turned ? "Turn the card to the front" : "Turn the card to the back"}>
            <RotateCw className="w-4 h-4" /> Turn the card
          </button>
        ) : <span />}
        {hasBack && <span className="pc-side" role="status" aria-live="polite">{turned ? "Back" : "Front"}</span>}
        <a className="pc-map" href={mapUrl} target="_blank" rel="noopener noreferrer" data-testid="modal-maps-link"
          onClick={(e) => e.stopPropagation()}>
          Map <ArrowUpRight className="w-3.5 h-3.5" />
        </a>
      </div>

      {(card?.sections?.length || place.tips) && (
        <div className="pc-guide">
          <h4 className="font-heading pc-guide-title">Make it your visit</h4>
          <p className="pc-guide-intro">Open what you need, when you need it.</p>
          {(card?.sections || []).map((s) => {
            const Answer = ANSWERS[s.id];
            if (!Answer) return null;
            const isOpen = open === s.id;
            return (
              <div className="pc-section" key={s.id} data-testid={`card-section-${s.id}`}>
                <button className="pc-question" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : s.id)}>
                  <span>{s.question}</span>
                  <span className="pc-sign" aria-hidden="true">{isOpen ? "−" : "+"}</span>
                </button>
                {isOpen && <div className="pc-answer">{Answer(s, setNextDoor)}<Links links={s.links} /></div>}
              </div>
            );
          })}
          {!card && place.tips && (
            <div className="pc-section"><div className="pc-answer" style={{ paddingTop: 14 }} data-testid="modal-tips">{place.tips}</div></div>
          )}
          {back.visitors_say && (
            <p className="pc-guide-intro" style={{ marginTop: 14 }}>
              <strong style={{ color: "var(--zen-sub)" }}>What visitors say. </strong>{back.visitors_say}
            </p>
          )}
        </div>
      )}
      {footer}
    </div>
  );
};

export default PlaceCard;
